<template>
    <div class="wrap">
        <div class="top">
            <div class="topInfo">
                <div class="topInfo__title">Same Usage. Lower Cost. One Simple Plan.</div>
                <div class="topInfo__desc">
                    You used
                    {{ $filters.floatNumber(currentPlanInfo.table.totalCredits, 'en-US') }} credits
                    {{ currentPlanInfo.price.period }}. This plan gives you the<br />
                    same, for less money and fewer invoices.
                </div>
            </div>

            <div v-if="showTabs" class="tabsWrap">
                <div class="tabs">
                    <div
                        v-for="tab in tabs"
                        :key="tab.key"
                        class="tabs__item"
                        :class="{
                            ['tabs__item--active']: tab.key === tabActive,
                        }"
                        @click="setActiveTab(tab.key)"
                    >
                        <span class="tabs__item__text">{{ tab.title }}</span>
                        <span v-if="tab.label" class="tabs__item__label">
                            {{ tab.label }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <div class="plansWrap">
            <div class="plans">
                <div class="plans--arrow">
                    <svg
                        width="72"
                        height="26"
                        viewBox="0 0 72 26"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <g clip-path="url(#clip0_93_8837)">
                            <path
                                d="M0 22.484C0.150439 21.5228 0.0752193 20.5616 0.376096 19.6569C0.827412 18.3565 1.42917 17.0561 2.3318 15.8688C7.82281 8.51852 15.7961 3.09067 26.5524 0.829057C37.5344 -1.43255 47.9147 0.37673 56.5649 6.25691C60.9276 9.19701 64.2373 12.759 66.7947 16.7169C67.246 17.4519 67.6974 18.1304 68.5248 19.3177C69.5026 17.678 70.2548 16.4342 71.007 15.1337C71.2327 15.1903 71.4583 15.1903 71.7592 15.2468C71.8344 15.6426 72.0601 16.0384 71.9849 16.4342C71.7592 18.4131 71.4583 20.392 71.1574 22.3709C70.9318 23.6713 70.7813 25.0283 68.9009 25.5937C66.9452 26.2156 65.2903 25.3675 64.0116 24.4063C62.2816 23.0494 60.702 21.5228 59.1976 19.9962C58.8967 19.6569 59.1976 19.035 59.1976 18.5261C59.8746 18.5261 60.702 18.3565 61.1533 18.5827C62.5072 19.3177 63.6355 20.2223 64.9142 21.127C64.9895 20.9574 65.1399 20.8443 65.0647 20.7877C59.1976 8.3489 47.1625 0.942137 30.0125 2.24256C29.1851 2.2991 28.3577 2.41218 27.5303 2.63834C16.0217 5.40881 7.52193 10.7801 2.3318 19.035C1.65482 20.1093 1.35395 21.3532 0.902631 22.5405C0.601753 22.5405 0.300877 22.484 0 22.484Z"
                                fill="#3EB786"
                            />
                        </g>
                        <defs>
                            <clipPath id="clip0_93_8837">
                                <rect width="72" height="26" fill="white" />
                            </clipPath>
                        </defs>
                    </svg>
                </div>

                <div class="plans__item" :class="{ custom: isCustomPlan }">
                    <div class="plans__title">
                        {{ currentPlanInfo.title }}
                    </div>

                    <div class="plans__status plans__status--error" :class="{ custom: isCustomPlan }">
                        {{ currentPlanInfo.textStatus }}
                    </div>

                    <div class="plans__price" :class="{ discount: hasDiscount }">
                        <div class="plans__price__wrap">
                            ${{ $filters.floatNumber(currentPlanInfo.price.total, 'en-US') }}
                            <span class="plans__price__sub">
                                / {{ currentPlanInfo.price.period }}
                            </span>
                        </div>
                        <div class="plans__price__info"></div>
                    </div>

                    <plan-item-table :table-data="currentPlanInfo.table" />

                    <div class="btnWrap">
                        <button class="btn" @click="currentPlanInfo.textButtonClick">
                            {{ currentPlanInfo.textButton }}
                        </button>
                    </div>
                </div>

                <div class="plans__item plans__item--suggestion" :class="{ custom: isCustomPlan }">
                    <div class="plans__title">
                        {{ suggestionPlanInfo.title }}
                    </div>

                    <div class="plans__status plans__status--success" :class="{ custom: isCustomPlan }">
                        {{ suggestionPlanInfo.textStatus }}
                    </div>

                    <template v-if="isCustomPlan">
                        <div class="plans__info">
                            <div class="plans__info__title custom">Want to speak<br/> to sales?</div>
                            <div class="plans__info__desc custom">
                                Based on your current usage, upgrading to a custom plan could save
                                you more and offer predictable costs. Let’s chat to find the best
                                solution for your needs!
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <div class="plans__price" :class="{ discount: hasDiscount }">
                            <div class="plans__price__wrap" :class="{ discount: hasDiscount }">
                                ${{
                                    $filters.floatNumber(
                                        suggestionPlanInfo.price.total,
                                        'en-US'
                                    )
                                }}
                                <span class="plans__price__sub">
                                    / {{ suggestionPlanInfo.price.period }}
                                </span>
                            </div>
                            <div v-if="hasDiscount" class="plans__price__wrap crossOut">
                                ${{
                                    $filters.floatNumber(
                                        suggestionPlanInfo.price.totalMonthly,
                                        'en-US'
                                    )
                                }}
                                / {{ suggestionPlanInfo.price.period }}
                            </div>
                            <div v-if="hasDiscount" class="plans__price__info">
                                Billed annually at ${{
                                    $filters.floatNumber(suggestedPlanTotalPrice, 'en-US')
                                }}
                            </div>
                        </div>

                        <plan-item-table :table-data="suggestionPlanInfo.table" />
                    </template>

                    <div class="btnWrap">
                        <button
                            :id="
                                suggestionPlanInfo.isChatWithSales
                                    ? 'intercom-custom-plan_modal-button'
                                    : null
                            "
                            class="btn btn--blue"
                            @click="suggestionPlanInfo.textButtonClick"
                        >
                            {{ suggestionPlanInfo.textButton }}
                        </button>
                    </div>

                    <div v-if="!isCustomPlan" class="planBottomInfo">
                        Need a different plan? <a @click="goToChoosePlans">See other plans</a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'pinia';
import stores from 'store/modules';
import { round } from 'lodash';
import { segmentEvent } from 'services/segment';
import filters from '../../../../vue/filters';
import PlanItemTable from './PlanItemTable.vue';

export default {
    components: {
        PlanItemTable,
    },

    emits: ['onCloseModal', 'goToUpgradePlan', 'goToChoosePlans'],

    data() {
        return {
            tabActive: 'yearly',
            tabs: [
                {
                    key: 'monthly',
                    title: 'Monthly',
                    label: '',
                },
                {
                    key: 'yearly',
                    title: 'Annually',
                    label: 'Get 2 months free',
                },
            ],
            periodPrice: {
                monthly: 'last 30 days',
                quarterly: 'last 3 months',
                'semi-annual': 'last 6 months',
                yearly: 'last 12 months',
            },
            periodTable: {
                monthly: 'mo',
                quarterly: '3 mo',
                'semi-annual': '6 mo',
                yearly: 'year',
            },
            periodSuggestion: {
                monthly: 'month',
                yearly: 'year',
            },
        };
    },

    computed: {
        suggestion() {
            return this.subscription.subscription_suggestion;
        },

        currentPlan() {
            return this.suggestion.current_plan;
        },

        currentPlanPeriod() {
            return this.suggestion.current_plan_period;
        },

        currentPlanType() {
            return this.suggestion.current_plan_type;
        },

        isCurrentPlanPeriodYearly() {
            return this.currentPlanPeriod === 'yearly';
        },

        isCurrentPlanPeriodMonthly() {
            return this.currentPlanPeriod === 'monthly';
        },

        isPeriodYearly() {
            return this.tabActive === 'yearly'
        },

        isPeriodMonthly() {
            return this.tabActive === 'monthly'
        },

        totalCreditsUsed() {
            return this.suggestion.bought_credits_amount + this.subscription.plan_credits;
        },

        currentPlanAdditionalCreditAmount() {
            let amount = this.subscription.plan.additional_credit_amount;
            if (this.currentPlanType === 'custom') {
                amount = round(amount * 100, 2);
            }
            return amount;
        },

        currentPlanAdditionalCreditAmountFormatted() {
            const amount = this.currentPlanAdditionalCreditAmount / 100;
            return this.formatDecimalPrice(amount);
        },

        currentPlanPrice() {
            let price = round(this.subscription.plan.price);
            if (this.currentPlanType === 'custom') {
                price = price / 100;
            }
            return price;
        },

        currentPlanExtraPrice() {
            return Math.ceil(this.suggestion.bought_credits_cost);
        },

        currentPlanTotalPrice() {
            return Math.ceil(this.currentPlanPrice + this.currentPlanExtraPrice);
        },

        currentPlanInfo() {
            return {
                title: 'Current plan',
                textStatus: 'You paid',
                textButton: 'Keep my current plan',
                textButtonClick: () => {
                    this.$emit('onCloseModal');
                },
                price: {
                    period: this.periodPrice[this.currentPlanPeriod],
                    total: this.currentPlanTotalPrice,
                },
                table: {
                    credits: this.subscription.plan_credits,
                    price: this.currentPlanPrice,
                    extraCredits: this.suggestion.bought_credits_amount,
                    extraPrice: this.currentPlanExtraPrice,
                    period: this.periodTable[this.currentPlanPeriod],
                    additionalCredit: this.currentPlanAdditionalCreditAmountFormatted,
                    totalCredits: this.totalCreditsUsed,
                },
            };
        },

        activeSuggestedPlan() {
            return this.suggestedPlans[this.tabActive];
        },

        suggestedPlanAdditionalCreditAmount() {
            if (!this.activeSuggestedPlan) return 0;
            return this.activeSuggestedPlan.additional_credit_amount / 100;
        },

        suggestedPlanAdditionalCreditAmountFormatted() {
            return this.formatDecimalPrice(this.suggestedPlanAdditionalCreditAmount);
        },

        suggestedPlanExtraCredits() {
            if (!this.activeSuggestedPlan) return 0;

            if (this.isCurrentPlanPeriodMonthly && this.isPeriodYearly) {
                const result = this.totalCreditsUsed * 12 - (this.activeSuggestedPlan?.credits || 0);

                return result > 0 ? result : 0;
            }
            const result = this.totalCreditsUsed - (this.activeSuggestedPlan?.credits || 0);

            return result > 0 ? result : 0;
        },

        suggestedPlanExtraPrice() {
            return Math.ceil(this.suggestedPlanExtraCredits * this.suggestedPlanAdditionalCreditAmountFormatted);
        },

        suggestedPlanTotalPrice() {
            if (!this.activeSuggestedPlan) return 0;
            return this.activeSuggestedPlan.price + this.suggestedPlanExtraPrice;
        },

        upgradeSaveCost() {
            if (this.isCustomPlan || !this.activeSuggestedPlan) return 0;

            if (this.isCurrentPlanPeriodMonthly && this.isPeriodYearly) {
                return (this.currentPlanTotalPrice * 12) - this.suggestedPlanTotalPrice;
            }

            return this.currentPlanTotalPrice - this.suggestedPlanTotalPrice;
        },

        suggestionPlanInfo() {
            if (this.isCustomPlan) {
                return {
                    title: 'Custom plan',
                    textStatus: 'Get a better deal',
                    textButton: 'Chat with sales',
                    isChatWithSales: true,
                    textButtonClick: () => {
                        this.$emit('onCloseModal');
                        this.sendEvent('Chat with Sales');
                    },
                };
            }

            let price =
                this.activeSuggestedPlan[this.isPeriodYearly ? 'monthly_price' : 'price'];
            let pricePeriod = 'monthly';
            let priceTotal = 0

            if (this.isCurrentPlanPeriodYearly) {
                price = this.suggestedPlans['yearly'].price;
                pricePeriod = 'yearly';
            }

            if ((this.isCurrentPlanPeriodYearly && this.isPeriodYearly) || (this.isCurrentPlanPeriodMonthly && this.isPeriodMonthly)) {
                priceTotal = price + this.suggestedPlanExtraPrice
            }

            if (this.isCurrentPlanPeriodMonthly && this.isPeriodYearly) {
                priceTotal = (this.activeSuggestedPlan.price + this.suggestedPlanExtraPrice) / 12
            }

            const monthlyPlan = this.suggestedPlans['monthly'];

            const monthlyExtraPrice = monthlyPlan && this.totalCreditsUsed > monthlyPlan.credits
                ? this.suggestedPlanAdditionalCreditAmountFormatted * (this.totalCreditsUsed - monthlyPlan.credits)
                : 0;

            return {
                title: 'Recommended plan',
                textStatus: `Get $${filters.floatNumber(this.upgradeSaveCost, 'en-US')} off`,
                textButton: 'Yes, I want this plan',
                upgradeSaveCost: this.upgradeSaveCost,
                textButtonClick: () => {
                    this.$emit('goToUpgradePlan', {
                        plan: this.activeSuggestedPlan,
                    });
                    this.sendEvent('Continue with Upgrade');
                },
                price: {
                    period: this.periodSuggestion[pricePeriod],
                    total: Math.ceil(priceTotal),
                    totalMonthly: monthlyPlan
                        ? Math.ceil(monthlyPlan.monthly_price + monthlyExtraPrice)
                        : 0,
                },
                table: {
                    extraCredits: this.suggestedPlanExtraCredits,
                    extraPrice: this.suggestedPlanExtraPrice,
                    credits: this.activeSuggestedPlan.credits,
                    period: this.periodTable[this.tabActive],
                    price: this.activeSuggestedPlan.price,
                    additionalCredit: this.suggestedPlanAdditionalCreditAmountFormatted,
                    additionalDiscount: round(
                        100 -
                            (this.suggestedPlanAdditionalCreditAmountFormatted /
                                this.currentPlanAdditionalCreditAmountFormatted) *
                                100,
                        1
                    ),
                },
            };
        },

        suggestedPlans() {
            if (this.isCurrentPlanPeriodYearly) {
                return {
                    yearly: this.billingPlans['annual'].find(
                        (item) =>
                            item.id === this.suggestion.suggested_plan ||
                            this.suggestion.suggested_annual_plan
                    ),
                };
            }

            return {
                yearly: this.billingPlans['annual'].find(
                    (item) => item.id === this.suggestion.suggested_annual_plan
                ),
                monthly: this.billingPlans['monthly'].find(
                    (item) => item.id === this.suggestion.suggested_plan
                ),
            };
        },

        isLastMonthly() {
            const plans = this.billingPlans['monthly'];
            return plans.findIndex((item) => item.id === this.currentPlan) === plans.length - 1;
        },

        isLastAnnual() {
            const plans = this.billingPlans['annual'];
            return plans.findIndex((item) => item.id === this.currentPlan) === plans.length - 1;
        },

        isCustomPlan() {
            return (
                this.currentPlanType === 'custom' ||
                this.isLastAnnual ||
                this.isLastMonthly ||
                (!this.suggestion.suggested_plan && !this.suggestion.suggested_annual_plan)
            );
        },

        showTabs() {
            return !this.isCustomPlan && !this.isCurrentPlanPeriodYearly;
        },

        hasDiscount() {
            return this.isPeriodYearly && this.suggestionPlanInfo?.price?.totalMonthly > 0;
        },

        ...mapState(stores.Subscriptions, ['subscription']),
        ...mapState(stores.BillingSources, ['billingPlans']),
    },

    methods: {
        formatDecimalPrice(value) {
            return Math.round(value * 10000) / 10000;
        },

        init() {
            this.initActiveTab();
            this.sendEvent('Offer Viewed');
        },

        initActiveTab() {
            if (this.isCurrentPlanPeriodYearly) {
                this.tabActive = 'yearly';
            }
        },

        sendEvent(action) {
            if (!this.suggestion) return;

            segmentEvent('Flag_Upgrade_Opportunity', {
                event_id: 'flag_upgrade_opportunity',
                plan_type: this.currentPlanType,
                'MRR/ARR': this.suggestionPlanInfo.upgradeSaveCost,
                action: action,
            });
        },

        setActiveTab(key) {
            this.tabActive = key;
        },

        goToChoosePlans() {
            this.$emit('goToChoosePlans');
            this.sendEvent('See other Plans');
        },
    },

    mounted() {
        this.init();
    },
};
</script>

<style lang="scss" scoped>
@import 'styles.module.scss';
</style>
